import React, { memo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { makeTableRow, generateAccessItemDrop, itemDropStatuses, checkStartCountIcon, constants } from '../../helpers';
import { isValidURL } from '../../../../utils/helper';

import TableBodyCheckbox from '../../../../components/Table/BodyCheck';
import Dropdown from '../../../../components/Dropdown/Default';
import Tooltip from '../../../../components/Tooltip';

import { ORDER_INFO } from '../../constants';

const TableRows = ({ orders, values, onChangeCheck, handleSelect, config, access, accessMassActions, queryString }) => {
  const { t } = useTranslation();

  const checkProvider = (item) => !!(access?.see_provider && item?.provider);

  return (
    <>
      {orders?.map((item, index) => (
        <Fragment key={index}>
          <tr className={values.includes(item.id) ? 'active' : ''}>
            {makeTableRow(item, config?.fields).map((elem, index) => {
              const itemDrop = generateAccessItemDrop(item?.access, config?.access, access, t);
              if (elem.access === false) {
                return null;
              }

              switch (elem.type) {
              case 'checkbox':
                const disabled = (Number(item.status) === constants.STATUS_CANCELED && Number(queryString.status) !== constants.STATUS_CANCELED)
                    || Number(item.status) === constants.STATUS_AWAITING
                    || accessMassActions.length === 0;
                return (
                  <TableBodyCheckbox
                    key={index}
                    rowSpan={2}
                    disabled={disabled}
                    item={item}
                    values={values}
                    onChangeCheck={onChangeCheck}
                    tdClass="table__orders-checker"
                  />
                );

              case 'actions':
                if (Number(item.status) === constants.STATUS_AWAITING || item.access.length === 0 || itemDrop.length === 0) {
                  return (
                    <td key={index} className="td-caret table__orders-actions" rowSpan={2}>
                      {' '}
                    </td>
                  );
                }

                return (
                  <td key={index} className="td-caret table__orders-actions" rowSpan={2}>
                    <Dropdown
                      handleSelect={(key) => handleSelect(key, item, 'single')}
                      size="xsmall"
                      title="Actions"
                      items={itemDrop}
                      forMassActions
                      classForBtnDrop="font__weight-med"
                      dropStyle="default"
                      toolbarClass="dropdown-right"
                      submenuStatuses={itemDropStatuses(config?.statuses, item.access.change_status)}
                      submenuPlacement="left"
                      user={item}
                      id={item.id}
                      item={item}
                    />
                  </td>
                );

              case 'link_url':
                return (
                  <td className="refill-link table__orders-link" key={index}>
                    <span>{item.link}</span>
                    {item.order_buttons
                        && item.order_buttons.length > 0
                        && item.order_buttons.map((order) => (
                          <Button
                            key={order.num + order.type}
                            bsSize="xsmall"
                            className="refill-button"
                            onClick={() => {
                              handleSelect(ORDER_INFO, {
                                ...item,
                                button_name: order.label,
                                button_type: order.type,
                                button_num: order.num,
                              });
                            }}
                          >
                            {order.label}
                          </Button>
                        ))}
                    {isValidURL(item.link) ? (
                      <a href={item.link_url} className="link-to-refill" rel="noopener noreferrer" target="_blank">
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                    ) : null}
                  </td>
                );

              case 'service':
                return (
                  <td key={index} rowSpan={checkProvider(item) ? 1 : 2} className="service__row table__orders-service">
                    <span className="service__id">{item.service_id}</span>
                    <span>{elem.value}</span>
                  </td>
                );

              case 'date':
                return (
                  <td key={index} rowSpan={2} className="table__orders-date">
                    {elem.value.split(' ').map((date, i) => (
                      <span key={i} className="nowrap">
                        {date}
                      </span>
                    ))}
                  </td>
                );

              case 'user':
                if (access?.see_user === false) {
                  return null;
                }

                return (
                  <td key={index} className="table__orders-user">
                    <span>{elem.value}</span>
                    {config?.fields?.api_type && item.type > 0 && (
                      <>
                        {' '}
                        <span className="label label-api">
                          {item.type_name === 'API'
                            ? item?.type_name
                            : item?.type_name?.charAt(0).toUpperCase() + item?.type_name?.slice(1).toLowerCase()}
                        </span>
                      </>
                    )}
                  </td>
                );

              case 'user_id':
                return (
                  <td key={index} className="table__orders-id">
                    <span>{elem.value}</span>
                  </td>
                );

              case 'charge':
                if (access?.see_charge === false) {
                  return null;
                }

                return (
                  <td key={index} className={elem.className}>
                    <span>{elem.value}</span>
                  </td>
                );

              case 'remains':
                return (
                  <td key={index} rowSpan={2} className="table__orders-remains">
                    <span>{elem.value}</span>
                    {item.is_autocomplete && (
                      <>
                        {' '}
                        <span className={`fas fa-check-circle${item.is_active_autocomplete ? ' grey' : ''}`} />
                      </>
                    )}
                  </td>
                );

              case 'start_count':
                const checkValue = checkStartCountIcon(elem.value, item.error_reason_start_count);

                return (
                  <td key={index} rowSpan={2} className="table__orders-start_count">
                    {checkValue.tooltip ? (
                      <Tooltip placement="top" tooltipClass="tooltip__top" text={checkValue.tooltipText}>
                        <span className={`${checkValue.icon}`} />
                      </Tooltip>
                    ) : (
                      <span>{checkValue.value}</span>
                    )}
                  </td>
                );

              case 'status_name':
                return (
                  <td key={index} rowSpan={2} className="table__orders-status">
                    <span>{elem.value}</span>
                  </td>
                );

              case 'mode_name':
                return (
                  <td key={index} rowSpan={2} className="table__orders-mode">
                    <span>{elem.value}</span>
                  </td>
                );

              case 'count':
                return (
                  <td key={index} rowSpan={2} className="table__orders-quantity">
                    <span>{elem.value}</span>
                  </td>
                );

              default:
                return (
                  <td key={index} rowSpan={2}>
                    <span>{elem.value}</span>
                  </td>
                );
              }
            })}
          </tr>

          <tr className="order-row-secondary">
            <td className="border-t-none p-t-0" colSpan={access?.see_user === false ? 1 : 2}>
              {access?.see_external_id && <div className="grey orders-pay-hash">{item.external_id || ''}</div>}
            </td>
            {access?.see_cost ? (
              <td className="border-t-none p-t-0 text-nowrap" colSpan={access?.see_charge ? 2 : 1}>
                <div className="grey">
                  {item.cost_original ? (
                    <Tooltip placement="left" text={String(item.cost_original)} tooltipClass="tooltip__left">
                      <span>
                        &asymp;
                        {item.cost_formatted || ''}
                      </span>
                    </Tooltip>
                  ) : (
                    <span>{item.cost_formatted || ''}</span>
                  )}
                </div>
              </td>
            ) : (
              <td className="border-t-none p-t-0" colSpan={2}>
                {' '}
              </td>
            )}
            {checkProvider(item) ? (
              <td className="border-t-none p-t-0">
                <div className="grey orders-pay-hash">{item?.provider || ''}</div>
              </td>
            ) : null}
          </tr>
        </Fragment>
      ))}
    </>
  );
};

TableRows.propTypes = {
  orders: PropTypes.array,
  values: PropTypes.array,
  onChangeCheck: PropTypes.func,
  handleSelect: PropTypes.func,
  config: PropTypes.object,
  access: PropTypes.object,
  accessMassActions: PropTypes.array,
  queryString: PropTypes.object,
};

const areEqual = (prevProps, nextProps) => {
  if (nextProps.isLoadingTable) {
    return true;
  }

  return (
    isEqual(prevProps.orders, nextProps.orders)
    && isEqual(prevProps.config, nextProps.config)
    && isEqual(prevProps.accessMassActions, nextProps.accessMassActions)
    && isEqual(prevProps.access, nextProps.access)
    && isEqual(prevProps.values, nextProps.values)
  );
};

export default memo(TableRows, areEqual);
